import { useState, useRef, useEffect } from 'react';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Send, Sparkles, Loader2, Plus, MessageCircle, Lightbulb, Trash2,
} from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { useIsMobile } from '@/hooks/use-mobile';
import { LiaMarkdown } from '@/components/lia/LiaMarkdown';

interface Message {
  id: string;
  role: 'user' | 'assistant';
  content: string;
}

interface ChatSession {
  id: string;
  title: string;
  created_at: string;
}

interface LiaChatDrawerProps {
  isOpen: boolean;
  onClose: () => void;
}

const QUICK_SUGGESTIONS = [
  '¿Cuál es mi nivel de madurez en IA?',
  '¿Qué proyectos tengo activos?',
  '¿Qué herramienta me recomiendas?',
];

export function LiaChatDrawer({ isOpen, onClose }: LiaChatDrawerProps) {
  const { user } = useAuth();
  const isMobile = useIsMobile();
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [sessions, setSessions] = useState<ChatSession[]>([]);
  const [showHistory, setShowHistory] = useState(false);
  const bottomRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (isOpen && user) {
      supabase
        .from('chat_sessions')
        .select('id, title, created_at')
        .eq('user_id', user.id)
        .eq('context_type', 'lia')
        .order('created_at', { ascending: false })
        .limit(30)
        .then(async ({ data }) => {
          if (data) setSessions(data);
          // Auto-load most recent session or show welcome
          if (!sessionId && messages.length === 0) {
            if (data && data.length > 0) {
              const latest = data[0];
              setSessionId(latest.id);
              const { data: msgs } = await supabase
                .from('chat_messages')
                .select('id, role, content')
                .eq('session_id', latest.id)
                .order('created_at');
              if (msgs && msgs.length > 0) {
                setMessages(msgs.map(m => ({
                  id: m.id,
                  role: m.role as 'user' | 'assistant',
                  content: m.content,
                })));
              } else {
                setMessages([{
                  id: 'welcome',
                  role: 'assistant',
                  content: '¡Hola! 👋 Soy **LIA**, tu asistente de inteligencia artificial. Tengo acceso a tu perfil, diagnóstico, proyectos y retos para darte respuestas personalizadas. ¿En qué te puedo ayudar?',
                }]);
              }
            } else {
              setMessages([{
                id: 'welcome',
                role: 'assistant',
                content: '¡Hola! 👋 Soy **LIA**, tu asistente de inteligencia artificial. Tengo acceso a tu perfil, diagnóstico, proyectos y retos para darte respuestas personalizadas. ¿En qué te puedo ayudar?',
              }]);
            }
          }
        });
    }
  }, [isOpen, user]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const loadSession = async (session: ChatSession) => {
    setSessionId(session.id);
    setShowHistory(false);
    const { data } = await supabase
      .from('chat_messages')
      .select('id, role, content')
      .eq('session_id', session.id)
      .order('created_at');
    if (data) {
      setMessages(data.map(m => ({
        id: m.id,
        role: m.role as 'user' | 'assistant',
        content: m.content,
      })));
    }
  };

  const startNewConversation = () => {
    setSessionId(null);
    setMessages([{
      id: 'welcome',
      role: 'assistant',
      content: '¡Hola! 👋 Soy **LIA**, tu asistente. ¿En qué te puedo ayudar?',
    }]);
    setShowHistory(false);
  };

  const deleteSession = async (e: React.MouseEvent, id: string) => {
    e.stopPropagation();
    try {
      await supabase.from('chat_messages').delete().eq('session_id', id);
      await supabase.from('chat_sessions').delete().eq('id', id);
      setSessions(prev => prev.filter(s => s.id !== id));
      if (sessionId === id) startNewConversation();
      toast.success('Conversación eliminada');
    } catch {
      toast.error('Error al eliminar');
    }
  };

  const handleSend = async (text?: string) => {
    const msg = text || input;
    if (!msg.trim() || isTyping) return;

    const userMsg: Message = { id: Date.now().toString(), role: 'user', content: msg };
    setMessages(prev => [...prev, userMsg]);
    setInput('');
    setIsTyping(true);

    try {
      const { data, error } = await supabase.functions.invoke('lia-chat', {
        body: { session_id: sessionId, message: msg },
      });
      if (error) throw error;

      if (data.session_id && !sessionId) {
        setSessionId(data.session_id);
        setSessions(prev => [{
          id: data.session_id,
          title: msg.substring(0, 100),
          created_at: new Date().toISOString(),
        }, ...prev]);
      }

      setMessages(prev => [...prev, {
        id: (Date.now() + 1).toString(),
        role: 'assistant',
        content: data.response || 'Lo siento, no pude generar una respuesta.',
      }]);
    } catch {
      setMessages(prev => [...prev, {
        id: (Date.now() + 1).toString(),
        role: 'assistant',
        content: '⚠️ Hubo un error al conectar con LIA. Intenta de nuevo.',
      }]);
    } finally {
      setIsTyping(false);
    }
  };

  return (
    <Sheet open={isOpen} onOpenChange={onClose}>
      <SheetContent
        side="right"
        className={cn(
          "p-0 flex flex-row max-w-[calc(100vw-48px)]",
          "w-full sm:max-w-[540px]",
          "lg:max-w-[1000px] xl:max-w-[1150px] 2xl:max-w-[1280px]"
        )}
      >
        {/* ── Sidebar de historial (desktop) ── */}
        {!isMobile && (
          <div className="w-64 border-r border-border flex flex-col bg-muted/30 shrink-0">
            <div className="p-4 border-b border-border flex items-center justify-between">
              <h3 className="text-sm font-semibold text-foreground">Conversaciones</h3>
              <Button variant="ghost" size="icon" className="h-7 w-7" onClick={startNewConversation} title="Nueva conversación">
                <Plus className="w-4 h-4" />
              </Button>
            </div>
            <ScrollArea className="flex-1">
              <div className="p-2 space-y-1">
                {sessions.length === 0 ? (
                  <p className="text-xs text-muted-foreground px-2 py-4">Sin conversaciones previas</p>
                ) : (
                  sessions.map(s => (
                    <div key={s.id} className="group flex items-center gap-1">
                      <button
                        onClick={() => loadSession(s)}
                        className={cn(
                          "flex-1 text-left px-3 py-2.5 rounded-lg text-sm hover:bg-muted transition-colors flex items-center gap-2 min-w-0",
                          sessionId === s.id && "bg-muted font-medium text-primary"
                        )}
                      >
                        <MessageCircle className="w-3.5 h-3.5 flex-shrink-0 text-muted-foreground" />
                        <span className="truncate">{s.title || 'Sin título'}</span>
                      </button>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-7 w-7 shrink-0 opacity-40 hover:opacity-100 transition-opacity"
                        onClick={(e) => deleteSession(e, s.id)}
                        title="Eliminar conversación"
                      >
                        <Trash2 className="w-3.5 h-3.5 text-destructive" />
                      </Button>
                    </div>
                  ))
                )}
              </div>
            </ScrollArea>
          </div>
        )}

        {/* ── Chat panel ── */}
        <div className="flex-1 flex flex-col min-w-0">
          {/* Header */}
          <SheetHeader className="p-4 border-b border-border bg-gradient-to-r from-primary/10 to-secondary/10 shrink-0">
            <div className="flex items-center justify-between">
              <SheetTitle className="flex items-center gap-2 text-lg">
                <div className="w-9 h-9 rounded-full bg-gradient-to-br from-primary to-secondary flex items-center justify-center">
                  <Sparkles className="w-5 h-5 text-white" />
                </div>
                Chat con LIA
              </SheetTitle>
              <div className="flex items-center gap-1">
                {isMobile && (
                  <>
                    <Button variant="ghost" size="icon" onClick={() => setShowHistory(!showHistory)} title="Historial">
                      <MessageCircle className="w-4 h-4" />
                    </Button>
                    <Button variant="ghost" size="icon" onClick={startNewConversation} title="Nueva conversación">
                      <Plus className="w-4 h-4" />
                    </Button>
                  </>
                )}
              </div>
            </div>
          </SheetHeader>

          {/* ── Panel de historial colapsable (mobile) ── */}
          {isMobile && showHistory && (
            <div className="border-b border-border p-3 max-h-60 overflow-y-auto bg-muted/30">
              <p className="text-xs font-medium text-muted-foreground mb-2">Conversaciones anteriores</p>
              {sessions.length === 0 ? (
                <p className="text-xs text-muted-foreground">Sin conversaciones previas</p>
              ) : (
                <div className="space-y-1">
                  {sessions.map(s => (
                    <div key={s.id} className="group flex items-center gap-1">
                      <button
                        onClick={() => loadSession(s)}
                        className={cn(
                          "flex-1 text-left px-3 py-2 rounded-lg text-sm hover:bg-muted transition-colors flex items-center gap-2 min-w-0",
                          sessionId === s.id && "bg-muted font-medium"
                        )}
                      >
                        <MessageCircle className="w-3 h-3 flex-shrink-0 text-muted-foreground" />
                        <span className="truncate">{s.title || 'Sin título'}</span>
                      </button>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-6 w-6 shrink-0 opacity-0 group-hover:opacity-100 transition-opacity"
                        onClick={(e) => deleteSession(e, s.id)}
                      >
                        <Trash2 className="w-3 h-3 text-destructive" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Messages */}
          <ScrollArea className="flex-1 p-4">
            <div className="space-y-4">
              {messages.map(m => (
                <div key={m.id} className={cn('flex', m.role === 'user' ? 'justify-end' : 'justify-start')}>
                  <div className={cn(
                    'max-w-[85%] rounded-2xl px-4 py-2 text-sm',
                    m.role === 'user'
                      ? 'bg-primary text-primary-foreground rounded-br-md'
                      : 'bg-muted text-foreground rounded-bl-md'
                  )}>
                    {m.role === 'assistant' ? (
                      <div className="prose prose-sm dark:prose-invert max-w-none [&>p]:mb-2 [&>ul]:mb-2 [&>ol]:mb-2 [&_table]:w-full [&_table]:border-collapse [&_th]:border [&_th]:border-border [&_th]:px-2 [&_th]:py-1 [&_th]:bg-muted [&_td]:border [&_td]:border-border [&_td]:px-2 [&_td]:py-1">
                        <LiaMarkdown text={m.content} onNavigate={onClose} />
                      </div>
                    ) : m.content}
                  </div>
                </div>
              ))}
              {isTyping && (
                <div className="flex justify-start">
                  <div className="bg-muted rounded-2xl rounded-bl-md px-4 py-2 text-sm flex items-center gap-2">
                    <Loader2 className="w-3 h-3 animate-spin" />
                    LIA está pensando...
                  </div>
                </div>
              )}
              <div ref={bottomRef} />
            </div>
          </ScrollArea>

          {/* Suggestions */}
          {messages.length <= 1 && (
            <div className="px-4 pb-2 shrink-0">
              <div className="flex items-center gap-1 text-xs text-muted-foreground mb-2">
                <Lightbulb className="w-3 h-3" /> Sugerencias
              </div>
              <div className="flex flex-wrap gap-2">
                {QUICK_SUGGESTIONS.map((s, i) => (
                  <Button key={i} variant="outline" size="sm" className="text-xs h-auto py-1 px-2" onClick={() => handleSend(s)}>
                    {s}
                  </Button>
                ))}
              </div>
            </div>
          )}

          {/* Input */}
          <div className="p-4 border-t border-border shrink-0">
            <div className="flex gap-2">
              <Input
                placeholder="Escribe tu pregunta..."
                value={input}
                onChange={e => setInput(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && handleSend()}
                disabled={isTyping}
                className="flex-1"
              />
              <Button onClick={() => handleSend()} size="icon" disabled={isTyping} className="bg-gradient-to-r from-primary to-secondary">
                <Send className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}
